import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator as CalculatorIcon } from 'lucide-react';

// Context Providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';

// Components
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';
import Modal from './components/UI/Modal';
import Calculator from './components/UI/Calculator';
import Button from './components/UI/Button';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import CustomersPage from './pages/Customers/CustomersPage';
import QuotationItemsPage from './pages/QuotationItems/QuotationItemsPage';
import JobsPage from './pages/Jobs/JobsPage';
import QuotationsPage from './pages/Quotations/QuotationsPage';
import CreateQuotationPage from './pages/Quotations/CreateQuotationPage';
import ViewQuotationPage from './pages/Quotations/ViewQuotationPage';
import RevisionQuotationPage from './pages/Quotations/RevisionQuotationPage';
import BOQsPage from './pages/BOQs/BOQsPage';
import CreateBOQPage from './pages/BOQs/CreateBOQPage';
import ViewBOQPage from './pages/BOQs/ViewBOQPage';
import RevisionBOQPage from './pages/BOQs/RevisionBOQPage';
import SettingsPage from './pages/Settings/SettingsPage';
import UsersPage from './pages/Users/UsersPage';
import TermsAndConditionsPage from './pages/TermsAndConditions/TermsAndConditionsPage';
import NotesPage from './pages/Notes/NotesPage';
import FootnotePage from './pages/Footnotes/FootnotePage';
// Master Data Pages
import BrandsPage from './pages/Brands/BrandsPage';
import SectionsPage from './pages/Sections/SectionsPage';
import CategoriesPage from './pages/Categories/CategoriesPage';
import TypesPage from './pages/Types/TypesPage';
import CompressorTypesPage from './pages/CompressorTypes/CompressorTypesPage';
import CountriesPage from './pages/Countries/CountriesPage';
import UnitsPage from './pages/Units/UnitsPage';
import VatsPage from './pages/Vats/VatsPage';
import AITsPage from './pages/AITs/AITsPage';
import SchedulesPage from './pages/Schedules/SchedulesPage';
import ProfitMarginsPage from './pages/ProfitMargins/ProfitMarginsPage';
import CustomsDutiesPage from './pages/CustomsDuties/CustomsDutiesPage';
import EmployeesPage from './pages/Employees/EmployeesPage';

// Create Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-blue-600 text-lg font-semibold" style={{ fontFamily: 'var(--font-family)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-blue-600 text-lg font-semibold" style={{ fontFamily: 'var(--font-family)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <ThemeProvider>
          <AuthProvider>
            <Router>
              {/* Floating Calculator Button */}
              <Button
                onClick={() => setCalculatorOpen(true)}
                className="fixed z-[9999] bottom-6 right-6 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] text-white rounded-full shadow-lg p-4 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.15)' }}
                aria-label="Open Calculator"
              >
                <CalculatorIcon className="w-7 h-7" />
              </Button>

              {/* Calculator Modal */}
              <Modal
                isOpen={calculatorOpen}
                onClose={() => setCalculatorOpen(false)}
                title="Calculator"
                size="sm"
                showCloseButton
              >
                <Calculator />
              </Modal>

              <AnimatePresence mode="wait">
                <Routes>
                  {/* Public Routes */}
                  <Route
                    path="/login"
                    element={
                      <PublicRoute>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <LoginPage />
                        </motion.div>
                      </PublicRoute>
                    }
                  />

                  {/* Protected Routes with Layout */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    
                    <Route
                      path="dashboard"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <DashboardPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="customers"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <CustomersPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="quotation-items"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <QuotationItemsPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="jobs"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <JobsPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="quotations"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <QuotationsPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="quotations/create"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <CreateQuotationPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="quotations/:id/revise"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <RevisionQuotationPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="quotations/:id/edit"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <CreateQuotationPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="quotations/:id"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ViewQuotationPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="boqs"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <BOQsPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="boqs/create"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <CreateBOQPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="boqs/edit/:id"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <CreateBOQPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="boqs/:id/revise"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <RevisionBOQPage />
                        </motion.div>
                      }
                    />
                    <Route
                      path="boqs/:id"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ViewBOQPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="settings"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <SettingsPage />
                        </motion.div>
                      }
                    />
                    
                    <Route
                      path="users"
                      element={
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.3 }}
                        >
                          <UsersPage />
                        </motion.div>
                      }
                    />
                    
                    {/* Master Data Routes */}
                    <Route path="brands" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><BrandsPage /></motion.div>} />
                    <Route path="sections" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><SectionsPage /></motion.div>} />
                    <Route path="categories" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><CategoriesPage /></motion.div>} />
                    <Route path="types" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><TypesPage /></motion.div>} />
                    <Route path="compressor-types" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><CompressorTypesPage /></motion.div>} />
                    <Route path="countries" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><CountriesPage /></motion.div>} />
                    <Route path="units" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><UnitsPage /></motion.div>} />
                    <Route path="vats" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><VatsPage /></motion.div>} />
                    <Route path="aits" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><AITsPage /></motion.div>} />
                    <Route path="schedules" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><SchedulesPage /></motion.div>} />
                    <Route path="profit-margins" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><ProfitMarginsPage /></motion.div>} />
                    <Route path="customs-duties" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><CustomsDutiesPage /></motion.div>} />
                    <Route path="terms-and-conditions" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><TermsAndConditionsPage /></motion.div>} />
                    <Route path="notes" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><NotesPage /></motion.div>} />
                    <Route path="footnotes" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><FootnotePage /></motion.div>} />
                    <Route path="employees" element={<motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}><EmployeesPage /></motion.div>} />
                  </Route>

                  {/* Catch-all route */}
                  <Route
                    path="*"
                    element={
                      <ProtectedRoute>
                        <div className="min-h-screen flex items-center justify-center">
                          <div className="text-center">
                            <h1 className="text-6xl font-bold text-gray-800 mb-4">404</h1>
                            <p className="text-xl text-gray-600 mb-8">Page not found</p>
                            <Button onClick={() => window.location.href = '/dashboard'}>
                              Go to Dashboard
                            </Button>
                          </div>
                        </div>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </AnimatePresence>

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    backgroundColor: '#1F2937',
                    color: '#FFFFFF',
                    fontFamily: 'var(--font-family)',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#10B981',
                      secondary: '#FFFFFF',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#EF4444',
                      secondary: '#FFFFFF',
                    },
                  },
                }}
              />
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
};

export default App;
